import React, { useMemo, useState } from "react";

/**
 * Props
 * - comments: array of comment objects (see sample data below)
 * - currentUser: { id?, name?, isHost? }
 * - onSubmitComment: ({ text, visibility, parentId }) => Promise<void> | void
 * - onLikeToggle: (commentId: string) => void
 * - onDeleteComment: (commentId: string) => void
 */
const InviteCommentSection = ({
  comments = [],
  currentUser = {},
  onSubmitComment,
  onLikeToggle,
  onDeleteComment,
  pendingDeleteIds = [],
}) => {
  const [newCommentText, setNewCommentText] = useState("");
  const [newCommentVisibility, setNewCommentVisibility] = useState("public");
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyVisibility, setReplyVisibility] = useState("public");

  const sortedComments = useMemo(() => {
    const clone = [...comments];
    // console.log(comments)
    return clone.sort(
      (a, b) => new Date(b.createdAt || b.timestamp || 0) - new Date(a.createdAt || a.timestamp || 0)
    );
  }, [comments]);

  const isHost = !!currentUser?.isHost;

  const handleNewCommentSubmit = () => {
    const trimmed = newCommentText.trim();
    if (!trimmed) return;
    onSubmitComment({
      text: trimmed,
      visibility: newCommentVisibility,
      parentId: null,
    });
    setNewCommentText("");
  };

  const handleReplySubmit = () => {
    if (!replyTarget) return;
    const trimmed = replyText.trim();
    if (!trimmed) return;
    onSubmitComment({
      text: trimmed,
      visibility: replyVisibility,
      parentId: replyTarget.commentId,
    });
    setReplyText("");
    setReplyTarget(null);
  };

  return (
    <section className="comments-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow-text">Warm memories</p>
          <h2>Leave a note</h2>
          <p>Public notes are visible to everyone, private notes stay between you and the host.</p>
        </div>
      </div>

      <div className="comment-form">
        <textarea
          placeholder="Share a memory, wish, or thanks..."
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          className="comment-input"
          rows={3}
        />
        <div className="comment-form-footer">
          <button
            type="button"
            className={`toggle-visibility ${newCommentVisibility === "private" ? "private" : ""}`}
            onClick={() =>
              setNewCommentVisibility((prev) => (prev === "public" ? "private" : "public"))
            }
          >
            {newCommentVisibility === "public" ? "Public" : "Private"}
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleNewCommentSubmit}
            disabled={!newCommentText.trim()}
          >
            Post
          </button>
        </div>
      </div>

      <div className="comments-list">
        {sortedComments.length === 0 && (
          <p className="muted-text">No responses yet. Be the first to drop a line!</p>
        )}
        {sortedComments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            depth={0}
            currentUser={currentUser}
            isHost={isHost}
            replyTarget={replyTarget}
            onStartReply={(c) => {
              if (c.depth >= 2) return;
              setReplyTarget({ commentId: c.id, depth: c.depth });
              setReplyVisibility("public");
              setReplyText("");
            }}
            replyText={replyText}
            setReplyText={setReplyText}
            replyVisibility={replyVisibility}
            setReplyVisibility={setReplyVisibility}
            onReplySubmit={handleReplySubmit}
            onLikeToggle={onLikeToggle}
            onDeleteComment={onDeleteComment}
            pendingDeleteIds={pendingDeleteIds}
          />
        ))}
      </div>
    </section>
  );
};

const CommentItem = ({
  comment,
  depth,
  currentUser,
  isHost,
  replyTarget,
  onStartReply,
  replyText,
  setReplyText,
  replyVisibility,
  setReplyVisibility,
  onReplySubmit,
  onLikeToggle,
  onDeleteComment,
  pendingDeleteIds,
}) => {
  const canViewPrivate =
    comment.visibility !== "private" ||
    isHost ||
    comment.author?.id === currentUser?.id ||
    currentUser?.isAdmin;

  if (!canViewPrivate) return null;

  const createdAt = comment.createdAt || comment.timestamp;
  const displayTime = createdAt ? new Date(createdAt).toLocaleString() : "Just now";
  const isReplying = replyTarget?.commentId === comment.id;
  const isDeleting = pendingDeleteIds.includes(comment.id);

  return (
    <div className={`comment-card depth-${depth}`}>
      <div className="comment-main">
        <div className="comment-meta">
          <strong>{comment.author?.name || "Guest"}</strong>
          <span className="muted-text">{displayTime}</span>
          {comment.visibility === "private" && (
            <span className="visibility-badge">Private</span>
          )}
        </div>
        <p className="comment-text">{comment.text}</p>
        <div className="comment-actions">
          <button
            type="button"
            className="text-link"
            onClick={() => onLikeToggle(comment.id)}
            disabled={isDeleting}
          >
            {comment.likedByCurrentUser ? "Unlike" : "Like"} ({comment.likes || 0})
          </button>
          {depth < 2 && (
            <button
              type="button"
              className="text-link"
              onClick={() => onStartReply({ id: comment.id, depth })}
              disabled={isDeleting}
            >
              Reply
            </button>
          )}
          {(comment.author?.id === currentUser?.id || isHost) && (
            <button
              type="button"
              className="text-link danger"
              disabled={isDeleting}
              onClick={() => onDeleteComment?.(comment.id)}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      </div>

      {isReplying && (
        <div className="comment-reply-form">
          <textarea
            rows={2}
            placeholder="Reply to this comment..."
            value={replyText}
            onChange={(event) => setReplyText(event.target.value)}
          />
          <div className="comment-form-footer">
            <button
              type="button"
              className={`toggle-visibility ${replyVisibility === "private" ? "private" : ""}`}
              onClick={() =>
                setReplyVisibility((prev) => (prev === "public" ? "private" : "public"))
              }
            >
              {replyVisibility === "public" ? "Public" : "Private"}
            </button>
            <button
              type="button"
              className="primary-btn secondary"
              onClick={onReplySubmit}
              disabled={!replyText.trim()}
            >
              Post Reply
            </button>
          </div>
        </div>
      )}

      {comment.replies &&
        comment.replies.map((reply) => (
          <CommentItem
            key={reply.id}
            comment={reply}
            depth={depth + 1}
            currentUser={currentUser}
            isHost={isHost}
            replyTarget={replyTarget}
            onStartReply={onStartReply}
            replyText={replyText}
            setReplyText={setReplyText}
            replyVisibility={replyVisibility}
            setReplyVisibility={setReplyVisibility}
            onReplySubmit={onReplySubmit}
            onLikeToggle={onLikeToggle}
            onDeleteComment={onDeleteComment}
            pendingDeleteIds={pendingDeleteIds}
          />
        ))}
    </div>
  );
};

export default InviteCommentSection;
